import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { QuestionCard } from './QuestionCard';
import { ResultsScreen } from './ResultsScreen';
import { Header } from './Header';
import { calculateRisk } from '../lib/findrisc-scoring';
import { generateFhirQuestionnaire, generateFhirQuestionnaireResponse } from '../lib/fhir-export';
import type { FindriscAnswers } from '../types/findrisc';

const TOTAL_QUESTIONS = 9;

export const FindriscSurvey: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<FindriscAnswers>({});
  const [showResults, setShowResults] = useState(false);

  const updateAnswer = useCallback((questionId: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < TOTAL_QUESTIONS - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const restartSurvey = useCallback(() => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  }, []);

  const saveToLocalStorage = useCallback(() => {
    const results = calculateRisk(answers);
    const savedData = {
      answers,
      results,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('findrisc-results', JSON.stringify(savedData));
  }, [answers]);

  const progress = ((currentStep + 1) / TOTAL_QUESTIONS) * 100;

  if (showResults) {
    const results = calculateRisk(answers);
    return (
      <div className="container mx-auto px-4 py-8">
        <ResultsScreen
          results={results}
          answers={answers}
          onRestart={restartSurvey}
          onSave={saveToLocalStorage}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card className="shadow-medical bg-gradient-card">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-primary">FINDRISC Assessment</CardTitle>
          <div className="space-y-2">
            <p className="text-muted-foreground">
              Question {currentStep + 1} of {TOTAL_QUESTIONS}
            </p>
            <Progress value={progress} className="h-2" />
          </div>
        </CardHeader>
        <CardContent>
          <QuestionCard
            step={currentStep}
            answers={answers}
            onAnswer={updateAnswer}
            onNext={nextStep}
            onPrev={prevStep}
            canGoNext={true}
            canGoPrev={currentStep > 0}
            totalQuestions={TOTAL_QUESTIONS}
          />
        </CardContent>
      </Card>
    </div>
  );
};